import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Badge,
  Button,
  FormControl,
  Grid2,
  MenuItem,
  Paper,
  Select,
  Stack
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import type { Weekdays } from "../../components/Pickers/WeekdayPicker/weekday-picker.component";
import { FilterSlideoutDialog } from "../../components/Reports/CapacityFilter/FilterSlideout/FilterSlideoutDialog.component";
import type { FilterTypState } from "../../components/Reports/CapacityFilter/DateAndTypeSelector/facility-report-filter-and-selector.component";
import { areDaysEqual } from "../../components/Reports/CapacityFilter/DateAndTypeSelector/functions/areWeekdaysEqual.function";
import { useRemoteFetchFloorByLocation } from "../../hooks/Remote/Floor/useRemoteFetchFloorByLocation";
import { useRemoteFetchFloorReport } from "../../hooks/Remote/Floor/useRemoteFetchFloorReport";
import { useRemoteFetchFloorPlan } from "../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlan";
import { useRemoteFetchLocations } from "../../hooks/Remote/Location/useRemoteFetchLocations";
import { BookingType } from "../Booking-Form/typings/booking-inputs";
import {
  getManagementInitialWeekdays,
  clampManagementWeekdaysToCompanySettings
} from "./utils/management-period-weekend.utils";
import {
  SelectedZoneOrWorkplaceType,
  setCapacityDataEmpty,
  setCapacitySelectedFloors,
  setCapacitySelectedWorkplaceOrZone,
  setCurrentFloors,
  setFilterSelected,
  setIsSelected,
  setSelectableEntitiesReplace,
  setSelectedBookingType,
  setSelectedByAttFilter,
  setSelectedLocation,
  setSelectedTimeframe,
  setSelectedZoneOrWorkplace
} from "../Reports/slices/report.slice";
import type {
  CapacityObject,
  FloorPlanMemo,
  ReportEntities
} from "../Reports/typings/reports.types";
import { ManagementBookingTypeToggle } from "./components/ManagementBookingTypeToggle";
import { ManagementCapaDayChart } from "./components/ManagementCapaDayChart";
import { ManagementCapaHourChart } from "./components/ManagementCapaHourChart";
import { ManagementCapaWeekdayChart } from "./components/ManagementCapaWeekdayChart";
import { ManagementCompareToolbarFields } from "./components/ManagementCompareToolbarFields";
import { ManagementDetailPageHeader } from "./components/ManagementDetailPageHeader";
import { ManagementFilterSelect } from "./components/ManagementFilterSelect";
import { ManagementFloorCompareSlot } from "./components/ManagementFloorCompareSlot";
import { ManagementFloorPlanPanel } from "./components/ManagementFloorPlanPanel";
import { ManagementLoadErrorAlert } from "./components/ManagementLoadErrorAlert";
import { ManagementPeriodNavigation } from "./components/ManagementPeriodNavigation";
import { ManagementToolbarField } from "./components/ManagementToolbarField";
import { ManagementWeekdaysToggle } from "./components/ManagementWeekdaysToggle";
import { parseManagementLocationIdFromSearch } from "./utils/management-location-detail-path.utils";
import {
  formatManagementLocationDisplayName,
  mapCapaDayChartData,
  mapCapaHourChartData,
  mapCapaWeekdayChartData,
  mergeCapaDayChartData,
  mergeCapaHourChartData,
  mergeCapaWeekdayChartData
} from "./utils/management-capa-chart.utils";
import {
  formatManagementPeriodLabel,
  ManagementPeriodMode
} from "./utils/management-period-picker.utils";
import { resolveManagementSelectionReportParams } from "./utils/management-selection-report.utils";
import { MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX } from "./utils/management-toolbar.constants";
import { managementToolbarSelectSx } from "./utils/management-toolbar.styles";
import { useManagementPrint } from "./hooks/useManagementPrint";

function formatFloorSeriesLabel(
  locationName: string | undefined,
  floorName: string | undefined,
  translate: (key: string) => string
): string {
  const locationLabel = formatManagementLocationDisplayName(locationName, translate);
  if (locationLabel && floorName) {
    return `${locationLabel} · ${floorName}`;
  }
  return floorName ?? locationLabel;
}

const EMPTY_PLACE_ZONE_SELECTION = {
  id: [] as number[],
  name: [] as string[],
  type: SelectedZoneOrWorkplaceType.ZONE,
  open: false,
  floorInventoryId: 0,
  bookingType: undefined as BookingType | undefined
};

/**
 * Management linked report: location occupancy detail (floor plan + capa charts).
 */
const ManagementLocationDetailPage: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const routerLocation = useLocation();
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.login.userInformation.sub ?? "");
  const companyId = useSelector(
    (state: RootState) => state.login.userInformation.companyId ?? null
  );
  const bookingsOnSaturdaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSaturdaysAllowed
  );
  const bookingsOnSundaysAllowed = useSelector(
    (state: RootState) => state.login.userInformation.company?.meta?.bookingsOnSundaysAllowed
  );
  const selectedZoneOrWorkplace = useSelector(
    (state: RootState) => state.report.selectedZoneOrWorkplace
  );
  const selectedByAttFilter = useSelector((state: RootState) => state.report.selectedByAttFilter);
  const {
    selectedWeekdays: storeWeekdays,
    selectedDepartmentIds,
    selectedAttributes
  } = useSelector((state: RootState) => state.report.filterSelected);
  const isHrAdmin = useSelector((state: RootState) => state.login.userRoles?.isHrAdmin ?? false);
  const capacitySelectedWorkplaceOrZone = useSelector(
    (state: RootState) => state.report.capacitySelectedWorkplaceOrZone
  );
  const reportIsLoading = useSelector((state: RootState) => state.report.isLoading);
  const workplaceCapacityRef = useRef(capacitySelectedWorkplaceOrZone);
  workplaceCapacityRef.current = capacitySelectedWorkplaceOrZone;
  const placeZoneSelectionOpenRef = useRef(false);
  placeZoneSelectionOpenRef.current = Boolean(
    selectedZoneOrWorkplace?.open && selectedZoneOrWorkplace.id.length > 0
  );

  const locationIdFromSearch = useMemo(
    () => parseManagementLocationIdFromSearch(routerLocation.search),
    [routerLocation.search]
  );

  const [locationId, setLocationId] = useState(() => locationIdFromSearch ?? "");
  const [floorId, setFloorId] = useState<number | null>(null);
  const [compareLocationId, setCompareLocationId] = useState("");
  const [compareFloorId, setCompareFloorId] = useState<number | null>(null);
  const [periodMode, setPeriodMode] = useState<ManagementPeriodMode>("week");
  const [periodAnchor, setPeriodAnchor] = useState(() => DateTime.now().startOf("week"));
  const [weekdays, setWeekdays] = useState<Weekdays>(
    getManagementInitialWeekdays({
      bookingsOnSaturdaysAllowed,
      bookingsOnSundaysAllowed
    })
  );
  const [bookingType, setBookingType] = useState<BookingType>(BookingType.WORKPLACE);
  const [floorPlanExpanded, setFloorPlanExpanded] = useState(false);
  const [filterSlideOpen, setFilterSlideOpen] = useState(false);

  const clearPlaceZoneSelection = useCallback(() => {
    dispatch(setIsSelected(false));
    dispatch(setSelectedZoneOrWorkplace(EMPTY_PLACE_ZONE_SELECTION));
  }, [dispatch]);

  const clearGroupFilters = useCallback(() => {
    dispatch(
      setFilterSelected({
        selectedDepartmentIds: [],
        selectedAttributes: []
      })
    );
    dispatch(
      setSelectedByAttFilter({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: -1,
        bookingType: undefined
      })
    );
  }, [dispatch]);

  const {
    data: locations,
    mutate: fetchLocations,
    isLoading: isLocationsLoading
  } = useRemoteFetchLocations();

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (locationIdFromSearch) {
      setLocationId(locationIdFromSearch);
    } else {
      setLocationId("");
    }
  }, [locationIdFromSearch]);

  const isAllLocations = locationId === "";
  const selectedLocation = useMemo(
    () => (locations ?? []).find(location => String(location.id) === locationId),
    [locationId, locations]
  );
  const isMobileWorkingLocation = Boolean(selectedLocation?.isMobileWorking);
  /** Charts without a floor plan: All locations or mobile working. */
  const isLocationCapacityMode = isAllLocations || isMobileWorkingLocation;

  const parsedLocationInventoryId =
    isAllLocations || Number.isNaN(Number(locationId)) ? undefined : Number(locationId);
  /** Floor list only for concrete office locations (not All / mobile working). */
  const floorLocationInventoryId = isLocationCapacityMode ? undefined : parsedLocationInventoryId;

  const { data: floors = [], isLoading: isFloorsLoading } =
    useRemoteFetchFloorByLocation(floorLocationInventoryId);

  const officeLocations = useMemo(
    () => (locations ?? []).filter(location => !location.isMobileWorking),
    [locations]
  );

  const allLocationInventoryIds = useMemo(
    () =>
      officeLocations
        .map(location => location.id)
        .filter((id): id is number => Number.isInteger(id)),
    [officeLocations]
  );

  const compareLocationInventoryId =
    compareLocationId === "" || Number.isNaN(Number(compareLocationId))
      ? undefined
      : Number(compareLocationId);

  const { data: compareFloors = [], isLoading: isCompareFloorsLoading } =
    useRemoteFetchFloorByLocation(compareLocationInventoryId);

  const periodStart = periodAnchor;
  const periodEnd =
    periodMode === "week" ? periodAnchor.plus({ days: 6 }) : periodAnchor.endOf("month");
  const startIso = periodStart.toISODate() ?? "";
  const endIso = periodEnd.toISODate() ?? "";

  /** SvgReport place/zone clicks require selectedTimeframe + weekdays on the report slice. */
  useEffect(() => {
    if (!startIso || !endIso) {
      return;
    }
    dispatch(
      setSelectedTimeframe({
        timeframeStart: startIso,
        timeframeEnd: endIso
      })
    );
  }, [dispatch, endIso, startIso]);

  useEffect(() => {
    dispatch(setFilterSelected({ selectedWeekdays: weekdays }));
  }, [dispatch, weekdays]);

  /** Slideout weekday edits write Redux first; keep toolbar state in sync. */
  useEffect(() => {
    if (!storeWeekdays) {
      return;
    }
    setWeekdays(prev => {
      const next = areDaysEqual(storeWeekdays, prev) ? prev : storeWeekdays;
      return clampManagementWeekdaysToCompanySettings(next, {
        bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed
      });
    });
  }, [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed, storeWeekdays]);

  useEffect(() => {
    setWeekdays(prev =>
      clampManagementWeekdaysToCompanySettings(prev, {
        bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed
      })
    );
  }, [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed]);

  useEffect(() => {
    dispatch(setSelectedBookingType(bookingType));
  }, [bookingType, dispatch]);

  useEffect(() => {
    clearPlaceZoneSelection();
    clearGroupFilters();
  }, [
    bookingType,
    clearGroupFilters,
    clearPlaceZoneSelection,
    endIso,
    floorId,
    locationId,
    startIso
  ]);

  /** Weekday / compare changes clear place/zone selection but keep department/attribute filters. */
  useEffect(() => {
    clearPlaceZoneSelection();
  }, [clearPlaceZoneSelection, compareFloorId, weekdays]);

  const hasConcreteFloor = floorId != null && Number.isInteger(floorId);
  const floorFetchEnabled =
    !isLocationCapacityMode && hasConcreteFloor && Boolean(startIso) && Boolean(endIso);
  const locationCapacityFilterIds = isAllLocations
    ? allLocationInventoryIds
    : parsedLocationInventoryId != null
      ? [parsedLocationInventoryId]
      : [];
  const locationCapacityEnabled =
    isLocationCapacityMode &&
    Boolean(startIso) &&
    Boolean(endIso) &&
    locationCapacityFilterIds.length > 0 &&
    !isLocationsLoading;
  const capacityFetchEnabled = isLocationCapacityMode ? locationCapacityEnabled : floorFetchEnabled;

  const hasCompareFloor = compareFloorId != null && Number.isInteger(compareFloorId);
  const compareFloorFetchEnabled =
    !isLocationCapacityMode && hasCompareFloor && Boolean(startIso) && Boolean(endIso);

  const placeZoneIds = selectedZoneOrWorkplace?.id ?? EMPTY_PLACE_ZONE_SELECTION.id;
  const placeZoneSelectionOpen = Boolean(
    selectedZoneOrWorkplace?.open && placeZoneIds.length > 0 && !isLocationCapacityMode
  );
  const placeZoneFilterType: SelectedZoneOrWorkplaceType =
    selectedZoneOrWorkplace?.type === SelectedZoneOrWorkplaceType.ZONE
      ? SelectedZoneOrWorkplaceType.ZONE
      : SelectedZoneOrWorkplaceType.WORKPLACE;
  const placeZoneBookingType = selectedZoneOrWorkplace?.bookingType ?? bookingType;

  const attFilterForPrimaryFloor = useMemo(
    () => selectedByAttFilter?.find(sel => sel.floorInventoryId === floorId && sel.open),
    [floorId, selectedByAttFilter]
  );

  const attributeFilterActive = Boolean(
    !placeZoneSelectionOpen &&
      !isLocationCapacityMode &&
      (selectedAttributes?.length ?? 0) > 0 &&
      attFilterForPrimaryFloor &&
      attFilterForPrimaryFloor.id.length > 0
  );
  let primaryFilterIds: number[] = [];
  if (attributeFilterActive && attFilterForPrimaryFloor) {
    primaryFilterIds = attFilterForPrimaryFloor.id;
  } else if (floorId != null) {
    primaryFilterIds = [floorId];
  }
  let primaryFilterType: "location" | "floor" | SelectedZoneOrWorkplaceType = "floor";
  if (attributeFilterActive && attFilterForPrimaryFloor) {
    primaryFilterType =
      attFilterForPrimaryFloor.type === SelectedZoneOrWorkplaceType.ZONE
        ? SelectedZoneOrWorkplaceType.ZONE
        : SelectedZoneOrWorkplaceType.WORKPLACE;
  } else if (isLocationCapacityMode) {
    primaryFilterType = "location";
  }
  const primaryBookingType =
    attributeFilterActive && attFilterForPrimaryFloor?.bookingType
      ? attFilterForPrimaryFloor.bookingType
      : bookingType;
  const departmentIds = selectedDepartmentIds?.length ? selectedDepartmentIds : undefined;

  const {
    data: floorPlan,
    isLoading: isFloorPlanLoading,
    isFetching: isFloorPlanFetching,
    isError: isFloorPlanError,
    refetch: refetchFloorPlan
  } = useRemoteFetchFloorPlan(floorId ?? 0, startIso, endIso, floorFetchEnabled);

  const {
    data: compareFloorPlan,
    isLoading: isCompareFloorPlanLoading,
    isFetching: isCompareFloorPlanFetching
  } = useRemoteFetchFloorPlan(compareFloorId ?? 0, startIso, endIso, compareFloorFetchEnabled);

  const selectionReportParams = useMemo(() => {
    if (!placeZoneSelectionOpen || floorId == null || !selectedZoneOrWorkplace) {
      return {
        filterIds: placeZoneIds,
        filterType: placeZoneFilterType,
        bookingType: placeZoneBookingType,
        calcPerWorkplace: true
      };
    }
    return resolveManagementSelectionReportParams({
      selectedZoneOrWorkplace,
      selectedBookingType: placeZoneBookingType,
      selectedAttributes,
      floorId,
      floorPlan,
      fallbackFilterType: placeZoneFilterType,
      fallbackBookingType: placeZoneBookingType,
      fallbackFilterIds: placeZoneIds
    });
  }, [
    floorId,
    floorPlan,
    placeZoneBookingType,
    placeZoneFilterType,
    placeZoneIds,
    placeZoneSelectionOpen,
    selectedAttributes,
    selectedZoneOrWorkplace
  ]);

  const placeZoneFetchEnabled =
    placeZoneSelectionOpen &&
    selectionReportParams.filterIds.length > 0 &&
    selectionReportParams.filterIds.every(id => Number.isInteger(id)) &&
    Boolean(startIso) &&
    Boolean(endIso);

  const {
    data: capacity,
    isLoading: isCapacityLoading,
    isFetching: isCapacityFetching,
    isError: isCapacityError,
    refetch: refetchCapacity
  } = useRemoteFetchFloorReport(
    {
      userId,
      calculateWithoutCosts: true,
      calcPerDay: true,
      calcPerWeekday: true,
      calcPerWorkplace: !isLocationCapacityMode,
      calcPerHour: true,
      reportType: "capacityManager",
      start: startIso,
      end: endIso,
      "filter[]": isLocationCapacityMode ? locationCapacityFilterIds : primaryFilterIds,
      filterType: primaryFilterType,
      companyId,
      bookingType: isLocationCapacityMode ? bookingType : primaryBookingType,
      weekdays,
      departmentIds
    },
    capacityFetchEnabled && (isLocationCapacityMode || primaryFilterIds.length > 0)
  );

  const {
    data: compareCapacity,
    isLoading: isCompareCapacityLoading,
    isFetching: isCompareCapacityFetching
  } = useRemoteFetchFloorReport(
    {
      userId,
      calculateWithoutCosts: true,
      calcPerDay: true,
      calcPerWeekday: true,
      calcPerWorkplace: true,
      calcPerHour: true,
      reportType: "capacityManager",
      start: startIso,
      end: endIso,
      "filter[]": compareFloorId != null ? [compareFloorId] : [],
      filterType: "floor",
      companyId,
      bookingType,
      weekdays,
      departmentIds
    },
    compareFloorFetchEnabled
  );

  const {
    data: selectionCapacityReport,
    isLoading: isSelectionCapacityLoading,
    isFetching: isSelectionCapacityFetching
  } = useRemoteFetchFloorReport(
    {
      userId,
      calculateWithoutCosts: true,
      calcPerDay: true,
      calcPerWeekday: true,
      calcPerWorkplace: selectionReportParams.calcPerWorkplace,
      calcPerHour: true,
      reportType: "capacityManager",
      start: startIso,
      end: endIso,
      "filter[]": selectionReportParams.filterIds,
      filterType: selectionReportParams.filterType,
      companyId,
      bookingType: selectionReportParams.bookingType,
      weekdays,
      departmentIds
    },
    placeZoneFetchEnabled
  );

  /** Keep report-slice workplace/zone capacity in sync for SvgReport greying/rates. */
  useEffect(() => {
    if (
      !placeZoneFetchEnabled ||
      !selectionCapacityReport ||
      selectionReportParams.filterIds.length === 0
    ) {
      return;
    }
    dispatch(
      setCapacitySelectedWorkplaceOrZone({
        id: selectionReportParams.filterIds[0],
        capacity: selectionCapacityReport
      })
    );
  }, [dispatch, placeZoneFetchEnabled, selectionCapacityReport, selectionReportParams.filterIds]);

  const isCapacityDetailLoading = capacityFetchEnabled && (isCapacityLoading || isCapacityFetching);
  const isSelectionDetailLoading =
    placeZoneFetchEnabled && (isSelectionCapacityLoading || isSelectionCapacityFetching);
  const isCompareDetailLoading =
    compareFloorFetchEnabled &&
    (isCompareFloorsLoading ||
      isCompareFloorPlanLoading ||
      isCompareFloorPlanFetching ||
      isCompareCapacityLoading ||
      isCompareCapacityFetching);

  const isDetailLoading = isLocationCapacityMode
    ? isCapacityDetailLoading
    : floorFetchEnabled &&
      (isFloorsLoading ||
        isFloorPlanLoading ||
        isFloorPlanFetching ||
        isCapacityDetailLoading ||
        isCompareDetailLoading ||
        isSelectionDetailLoading);

  useEffect(() => {
    if (isLocationCapacityMode) {
      setFloorId(null);
      setFloorPlanExpanded(false);
      setCompareLocationId("");
      setCompareFloorId(null);
      return;
    }
    if (floors.length === 0) {
      setFloorId(null);
      setFloorPlanExpanded(false);
      return;
    }
    setFloorId(prev => {
      if (prev != null && floors.some(floor => floor.id === prev)) {
        return prev;
      }
      return floors[0]?.id ?? null;
    });
  }, [floors, isLocationCapacityMode]);

  useEffect(() => {
    if (compareLocationInventoryId == null) {
      setCompareFloorId(null);
      return;
    }
    setCompareFloorId(prev => {
      if (prev != null && compareFloors.some(floor => floor.id === prev)) {
        return prev;
      }
      return null;
    });
  }, [compareFloors, compareLocationInventoryId]);

  useEffect(() => {
    if (isLocationCapacityMode) {
      setFloorPlanExpanded(false);
      return;
    }
    if (isDetailLoading) {
      return;
    }
    if (floorFetchEnabled && floorPlan && capacity) {
      setFloorPlanExpanded(true);
    }
  }, [capacity, floorFetchEnabled, floorPlan, isDetailLoading, isLocationCapacityMode]);

  const compareLocation = useMemo(
    () => officeLocations.find(location => String(location.id) === compareLocationId),
    [compareLocationId, officeLocations]
  );

  const facilityFloorData = useMemo((): FloorPlanMemo | undefined => {
    if (floorId == null || !floorPlan) {
      return undefined;
    }
    const memo: FloorPlanMemo = {
      [floorId]: {
        floorData: floorPlan,
        picturePath: floorPlan.outlineUrl ?? ""
      }
    };
    if (compareFloorId != null && compareFloorPlan) {
      memo[compareFloorId] = {
        floorData: compareFloorPlan,
        picturePath: compareFloorPlan.outlineUrl ?? ""
      };
    }
    return memo;
  }, [compareFloorId, compareFloorPlan, floorId, floorPlan]);

  const facilityEntityData = useMemo((): CapacityObject[] | undefined => {
    if (floorId == null || !capacity?.capacityPerformancePerWorkplace) {
      return undefined;
    }
    const entities: CapacityObject[] = [{ id: floorId, capacity }];
    if (compareFloorId != null && compareCapacity?.capacityPerformancePerWorkplace) {
      entities.push({ id: compareFloorId, capacity: compareCapacity });
    }
    return entities;
  }, [capacity, compareCapacity, compareFloorId, floorId]);

  /** FacilityReportSVG / SvgReport read currentFloors + capacitySelectedFloors from report slice. */
  useEffect(() => {
    return () => {
      clearPlaceZoneSelection();
      dispatch(setCurrentFloors([{ id: -1, open: false }]));
      dispatch(setCapacityDataEmpty(true));
    };
  }, [clearPlaceZoneSelection, dispatch]);

  useEffect(() => {
    if (isLocationCapacityMode || floorId == null || !capacity || !floorPlan) {
      clearPlaceZoneSelection();
      dispatch(setCurrentFloors([{ id: -1, open: false }]));
      dispatch(setCapacityDataEmpty(true));
      return;
    }

    const locationNumericId = parsedLocationInventoryId ?? -1;
    const mergedFloors = [
      ...floors.map(floor => ({ name: floor.name, inventoryId: floor.id })),
      ...compareFloors.map(floor => ({ name: floor.name, inventoryId: floor.id }))
    ];
    const reportEntity: ReportEntities = {
      id: locationNumericId,
      locations: selectedLocation?.name
        ? [{ name: selectedLocation.name, inventoryId: locationNumericId }]
        : [],
      floors: mergedFloors,
      zones: [],
      places: []
    };

    const openFloors = [{ id: floorId, open: true }];
    if (compareFloorId != null && compareFloorPlan && compareCapacity) {
      openFloors.push({ id: compareFloorId, open: true });
    }

    const preserveWorkplaceCapacity = placeZoneSelectionOpenRef.current
      ? workplaceCapacityRef.current
      : undefined;

    dispatch(setCapacityDataEmpty(true));
    dispatch(setCurrentFloors(openFloors));
    dispatch(setCapacitySelectedFloors({ id: floorId, capacity }));
    if (compareFloorId != null && compareCapacity) {
      dispatch(setCapacitySelectedFloors({ id: compareFloorId, capacity: compareCapacity }));
    }
    if (preserveWorkplaceCapacity) {
      dispatch(setCapacitySelectedWorkplaceOrZone(preserveWorkplaceCapacity));
    }
    dispatch(
      setSelectedLocation({
        id: locationNumericId,
        open: true,
        sub: userId
      })
    );
    dispatch(setSelectableEntitiesReplace([reportEntity]));
  }, [
    capacity,
    clearPlaceZoneSelection,
    compareCapacity,
    compareFloorId,
    compareFloorPlan,
    compareFloors,
    dispatch,
    floorId,
    floorPlan,
    floors,
    isLocationCapacityMode,
    parsedLocationInventoryId,
    selectedLocation?.name,
    userId
  ]);

  const periodLabel = useMemo(
    () => formatManagementPeriodLabel(periodMode, periodAnchor, i18n.language),
    [i18n.language, periodAnchor, periodMode]
  );

  const weekendSettings = useMemo(
    () => ({
      bookingsOnSaturdaysAllowed,
      bookingsOnSundaysAllowed
    }),
    [bookingsOnSaturdaysAllowed, bookingsOnSundaysAllowed]
  );

  const selectionCapacity = selectionCapacityReport ?? capacitySelectedWorkplaceOrZone?.capacity;
  const selectionSeriesName = placeZoneSelectionOpen
    ? selectedZoneOrWorkplace.name.join(", ")
    : undefined;

  const weekdayChartData = useMemo(() => {
    if (placeZoneSelectionOpen) {
      return mapCapaWeekdayChartData(selectionCapacity, weekendSettings);
    }
    const primary = mapCapaWeekdayChartData(capacity, weekendSettings);
    const compare = compareFloorFetchEnabled
      ? mapCapaWeekdayChartData(compareCapacity, weekendSettings)
      : undefined;
    return mergeCapaWeekdayChartData(primary, compare);
  }, [
    capacity,
    compareCapacity,
    compareFloorFetchEnabled,
    placeZoneSelectionOpen,
    selectionCapacity,
    weekendSettings
  ]);

  const hourChartData = useMemo(() => {
    if (placeZoneSelectionOpen) {
      return mapCapaHourChartData(selectionCapacity);
    }
    const primary = mapCapaHourChartData(capacity);
    const compare = compareFloorFetchEnabled ? mapCapaHourChartData(compareCapacity) : undefined;
    return mergeCapaHourChartData(primary, compare);
  }, [
    capacity,
    compareCapacity,
    compareFloorFetchEnabled,
    placeZoneSelectionOpen,
    selectionCapacity
  ]);

  const dayChartData = useMemo(() => {
    if (placeZoneSelectionOpen) {
      return mapCapaDayChartData(selectionCapacity, weekendSettings);
    }
    const primary = mapCapaDayChartData(capacity, weekendSettings);
    const compare = compareFloorFetchEnabled
      ? mapCapaDayChartData(compareCapacity, weekendSettings)
      : undefined;
    return mergeCapaDayChartData(primary, compare);
  }, [
    capacity,
    compareCapacity,
    compareFloorFetchEnabled,
    placeZoneSelectionOpen,
    selectionCapacity,
    weekendSettings
  ]);

  const showFloorControls = !isLocationCapacityMode;
  const selectedFloorName = floors.find(floor => floor.id === floorId)?.name;
  const compareFloorName = compareFloors.find(floor => floor.id === compareFloorId)?.name;
  const primarySeriesName = placeZoneSelectionOpen
    ? selectionSeriesName
    : formatFloorSeriesLabel(selectedLocation?.name, selectedFloorName, t);
  const compareSeriesName =
    placeZoneSelectionOpen || !hasCompareFloor
      ? undefined
      : formatFloorSeriesLabel(compareLocation?.name, compareFloorName, t);
  const initialLoading = Boolean(showFloorControls && isDetailLoading && !floorPlanExpanded);
  const overlayLoading = Boolean(
    showFloorControls &&
      ((isDetailLoading && floorPlanExpanded) ||
        (placeZoneSelectionOpen && (reportIsLoading || isSelectionDetailLoading)))
  );
  const showCharts = placeZoneSelectionOpen
    ? Boolean(selectionCapacity)
    : capacityFetchEnabled && !isDetailLoading && Boolean(capacity);
  const capaEmptyMessageKey = showCharts ? "_management_chartEmpty" : "_management_capaIdle";
  const floorPlanCollapsedHint = isAllLocations
    ? t("_management_floorPlanCollapsedHint")
    : undefined;

  const clearCompare = () => {
    clearPlaceZoneSelection();
    setCompareLocationId("");
    setCompareFloorId(null);
  };

  const isGroupFilterApplied =
    (selectedAttributes?.length ?? 0) > 0 || (selectedDepartmentIds?.length ?? 0) > 0;

  const slideoutFilterType = useMemo((): FilterTypState[] => {
    const types: FilterTypState[] = [placeZoneSelectionOpen ? placeZoneFilterType : "floor"];
    if (hasCompareFloor) {
      types.push("floor");
    }
    return types;
  }, [hasCompareFloor, placeZoneFilterType, placeZoneSelectionOpen]);

  const slideoutBasicParams = useMemo(
    () => ({
      userId,
      calculateWithoutCosts: true,
      calcPerDay: true,
      calcPerWeekday: true,
      calcPerWorkplace: true,
      calcPerHour: true,
      reportType: "capacityManager",
      start: startIso,
      end: endIso,
      companyId,
      bookingType,
      weekdays
    }),
    [bookingType, companyId, endIso, startIso, userId, weekdays]
  );

  const canOpenFilterSlideout = Boolean(
    showFloorControls && facilityFloorData && facilityEntityData && floorPlanExpanded
  );

  const compareSelectProps = {
    locations: officeLocations
      .filter((location): location is typeof location & { id: number } =>
        Number.isInteger(location.id)
      )
      .map(location => ({
        id: location.id,
        name: location.name
      })),
    floors: compareFloors.map(floor => ({ id: floor.id, name: floor.name })),
    locationId: compareLocationId,
    floorId: compareFloorId,
    excludedFloorId: floorId,
    locationsLoading: isLocationsLoading,
    floorsLoading: isCompareFloorsLoading,
    onLocationChange: setCompareLocationId,
    onFloorChange: setCompareFloorId
  };

  const shiftPeriod = (direction: -1 | 1) => {
    setPeriodAnchor(prev =>
      periodMode === "week" ? prev.plus({ weeks: direction }) : prev.plus({ months: direction })
    );
  };

  const handlePeriodSelect = (mode: ManagementPeriodMode, anchor: DateTime) => {
    setPeriodMode(mode);
    setPeriodAnchor(anchor);
  };

  const showLocationFetchError =
    (isCapacityError || (isFloorPlanError && floorFetchEnabled)) && capacityFetchEnabled;

  const { contentRef, handlePrint } = useManagementPrint(t("_management_locationDetailTitle"));

  const retryLocationDetailFetch = () => {
    const retries: Array<Promise<unknown>> = [];
    if (isCapacityError) {
      retries.push(refetchCapacity());
    }
    if (isFloorPlanError && floorFetchEnabled) {
      retries.push(refetchFloorPlan());
    }
    Promise.all(retries).catch(() => undefined);
  };

  return (
    <Box ref={contentRef} data-testid="management-location-detail-page" sx={{ width: "100%" }}>
      <ManagementDetailPageHeader
        title={t("_management_locationDetailTitle")}
        onPrint={handlePrint}
      />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", mb: 2 }}>
        <Grid2 container spacing={2} alignItems="flex-end">
          <Grid2 size={{ xs: 12, md: showFloorControls ? 3 : 4 }}>
            <ManagementPeriodNavigation
              periodMode={periodMode}
              periodAnchor={periodAnchor}
              periodLabel={periodLabel}
              onPrevious={() => shiftPeriod(-1)}
              onNext={() => shiftPeriod(1)}
              onSelect={handlePeriodSelect}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: showFloorControls ? 2 : 3 }}>
            <ManagementFilterSelect
              label={t("Location")}
              labelId="management-location-select-label"
              options={(locations ?? []).map(location => ({
                id: String(location.id),
                name: formatManagementLocationDisplayName(location.name, t)
              }))}
              allLabel={t("All locations")}
              value={locationId}
              onChange={setLocationId}
              disabled={isLocationsLoading}
              data-testid="management-location-select"
            />
          </Grid2>
          {showFloorControls && (
            <Grid2 size={{ xs: 12, md: 2 }}>
              <ManagementToolbarField label={t("Floor")} labelId="management-floor-select-label">
                <FormControl fullWidth size="small" sx={managementToolbarSelectSx}>
                  <Select
                    labelId="management-floor-select-label"
                    value={floorId ?? ""}
                    onChange={event => setFloorId(Number(event.target.value))}
                    data-testid="management-floor-select"
                    disabled={isFloorsLoading || floors.length === 0}
                  >
                    {floors.map(floor => (
                      <MenuItem key={floor.id} value={floor.id}>
                        {floor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ManagementToolbarField>
            </Grid2>
          )}
          <Grid2 size={{ xs: 12, md: 2 }}>
            <ManagementBookingTypeToggle value={bookingType} onChange={setBookingType} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ManagementWeekdaysToggle weekdays={weekdays} onChange={setWeekdays} />
              </Box>
              {canOpenFilterSlideout ? (
                <Box sx={{ flexShrink: 0 }}>
                  <ManagementToolbarField label={t("Filters")} labelId="management-filters-label">
                    <Badge
                      color="primary"
                      variant="dot"
                      overlap="circular"
                      invisible={!isGroupFilterApplied}
                      data-testid="management-filters-applied-badge"
                      sx={{
                        "& .MuiBadge-badge": {
                          top: 6,
                          right: 6
                        }
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        color={isGroupFilterApplied ? "primary" : "inherit"}
                        onClick={() => setFilterSlideOpen(true)}
                        aria-label={
                          isGroupFilterApplied
                            ? t("_management_filtersOpenApplied")
                            : t("_management_filtersOpen")
                        }
                        data-testid="management-filter-slide-open-btn"
                        sx={{
                          height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
                          minWidth: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
                          px: 1.25,
                          borderColor: isGroupFilterApplied ? "primary.main" : "divider",
                          color: isGroupFilterApplied ? "primary.main" : "text.primary",
                          bgcolor: "background.paper",
                          "&:hover": {
                            borderColor: isGroupFilterApplied ? "primary.main" : "text.secondary",
                            bgcolor: "action.hover"
                          }
                        }}
                      >
                        <FilterAltIcon fontSize="small" />
                      </Button>
                    </Badge>
                  </ManagementToolbarField>
                </Box>
              ) : null}
            </Stack>
          </Grid2>
        </Grid2>
        {showFloorControls && hasCompareFloor && (
          <ManagementCompareToolbarFields {...compareSelectProps} />
        )}
      </Paper>

      {canOpenFilterSlideout && facilityFloorData && facilityEntityData ? (
        <FilterSlideoutDialog
          isOpen={filterSlideOpen}
          setIsOpen={setFilterSlideOpen}
          setFetchEnabled={() => undefined}
          isHrAdmin={isHrAdmin}
          floorData={facilityFloorData}
          filterType={slideoutFilterType}
          basicParams={slideoutBasicParams}
          entityData={facilityEntityData}
          skipReportFetch
          hideWeekdayTab
        />
      ) : null}

      <Stack spacing={2}>
        {!isMobileWorkingLocation && (
          <ManagementFloorPlanPanel
            expanded={floorPlanExpanded}
            loading={initialLoading}
            loadingOverlay={overlayLoading}
            floorName={
              formatFloorSeriesLabel(selectedLocation?.name, selectedFloorName, t) || undefined
            }
            compareFloorName={
              hasCompareFloor
                ? formatFloorSeriesLabel(compareLocation?.name, compareFloorName, t) || undefined
                : undefined
            }
            collapsedHint={floorPlanCollapsedHint}
            floorData={facilityFloorData}
            entityData={facilityEntityData}
            compareSlot={
              showFloorControls ? <ManagementFloorCompareSlot {...compareSelectProps} /> : undefined
            }
            compareActive={hasCompareFloor}
            onClearCompare={clearCompare}
            onClearPlaceZoneSelection={clearPlaceZoneSelection}
          />
        )}

        {showLocationFetchError && (
          <ManagementLoadErrorAlert
            messageKey="_management_locationDetailLoadError"
            onRetry={retryLocationDetailFetch}
            testId="management-location-fetch-error"
          />
        )}

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", height: "100%" }}>
              <ManagementCapaWeekdayChart
                data={showCharts ? weekdayChartData : []}
                emptyMessageKey={capaEmptyMessageKey}
                primarySeriesName={showFloorControls ? primarySeriesName : undefined}
                compareSeriesName={compareSeriesName || undefined}
              />
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", height: "100%" }}>
              <ManagementCapaHourChart
                data={showCharts ? hourChartData : []}
                emptyMessageKey={capaEmptyMessageKey}
                primarySeriesName={showFloorControls ? primarySeriesName : undefined}
                compareSeriesName={compareSeriesName || undefined}
              />
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
              <ManagementCapaDayChart
                data={showCharts ? dayChartData : []}
                emptyMessageKey={capaEmptyMessageKey}
                primarySeriesName={showFloorControls ? primarySeriesName : undefined}
                compareSeriesName={compareSeriesName || undefined}
              />
            </Paper>
          </Grid2>
        </Grid2>
      </Stack>
    </Box>
  );
};

export default ManagementLocationDetailPage;
